
import {
  computed,
  defineComponent, inject, onMounted, ref, watch,
} from 'vue';
import { useRouter } from 'vue-router';
import { Form, defineRule } from 'vee-validate';
// eslint-disable-next-line import/no-cycle
import { useMainStore } from '@/store/mainStore';
import AppInput from '@/components/UI/AppInput.vue';
import AppButton from '@/components/UI/AppButton.vue';
import AppCheckbox from '@/components/UI/AppCheckbox.vue';
import AppAlert from '@/components/UI/AppAlert.vue';
import ValidationService from '@/services/ValidationService';
// eslint-disable-next-line import/no-cycle
import { useRegistrationStore } from '../store/registrationStore';
import { PASSWORD_LENGTH } from '@/types/consts';
import AppLoader from '@/components/UI/AppLoader.vue';
// eslint-disable-next-line import/no-cycle
import MonitoringService from '@/services/MonitoringService';
// eslint-disable-next-line import/no-cycle
import RegistrationService from '@/modules/registration/services/RegistrationService';

defineRule('required', (value:string) => ValidationService.isNotEmpty(value) || 'This field is required');

export default defineComponent({
  components: {
    Form,
    AppInput,
    AppButton,
    AppAlert,
    AppLoader,
    AppCheckbox,
  },
  emits: ['on-submit'],
  setup() {
    // eslint-disable-next-line @typescript-eslint/ban-types
    const $localize = inject('$localize') as Function;

    const registrationStore = useRegistrationStore();
    const createInProgress = ref(false);
    const mainStore = useMainStore();

    const errorKey = ref('');
    const emailValue = ref<string>(registrationStore.credentials.email);
    const passwordValue = ref('');
    const hippaValue = ref(undefined);
    const isFormTouched = ref(false);

    const router = useRouter();
    const schema = {
      email: 'required',
      password: 'required',
    };

    const inputEmail = ref<any | null>(null);
    const emailError = ref('');
    const passwordError = ref('');
    const emailInputValue = computed(() => inputEmail.value?.value);
    watch(() => emailInputValue.value, (nv, ov) => {
      if ((nv !== ov) && (ov !== null)) {
        emailError.value = '';
        errorKey.value = '';
      }
    });
    watch(() => passwordValue.value, (nv, ov) => {
      if ((nv !== ov) && (ov !== null)) {
        passwordError.value = '';
        errorKey.value = '';
      }
    });

    const passwordRequirements = computed(() => ({
      isLonger: ValidationService.isLonger(passwordValue.value, PASSWORD_LENGTH),
      containsCapitalLetter: ValidationService.containsCapitalLetter(passwordValue.value),
      containsSpecialCharacter: ValidationService.containsSpecialCharacter(passwordValue.value),
      containsNumeric: ValidationService.containsNumeric(passwordValue.value),
    }));

    function onBeforeSubmit(values) {
      let error = false;

      if (!ValidationService.isEmail(values.email)) {
        emailError.value = $localize('error.email');
        error = true;
      }
      if (!ValidationService.isLonger(values.password, PASSWORD_LENGTH)) {
        passwordError.value = 'Password is not valid, should be not less then 6 characters';
        error = true;
      }
      if (!ValidationService.containsCapitalLetter(values.password)) {
        passwordError.value = 'Password is not valid, should contain: 1 uppercase letter';
        error = true;
      }
      if (!ValidationService.containsSpecialCharacter(values.password)) {
        passwordError.value = 'Password is not valid, should contain: 1 special character';
        error = true;
      }
      if (!ValidationService.containsNumeric(values.password)) {
        passwordError.value = 'Password is not valid, should contain: 1 number';
        error = true;
      }
      return !error;
    }

    function nextStep(values) {
      /** fix autocomplete error on IOS devices */
      const inp = document.getElementById('passInput') as HTMLInputElement;
      if (inp) {
        // eslint-disable-next-line no-param-reassign
        values.password = inp.value;
      }

      isFormTouched.value = true;
      const isFormValid = onBeforeSubmit(values);
      if (!isFormValid) return;
      createInProgress.value = true;
      registrationStore.credentials.email = emailValue.value;
      registrationStore.credentials.password = passwordValue.value;
      registrationStore.credentials.hippa = hippaValue.value;
      registrationStore.createUser.hippa = hippaValue.value;

      RegistrationService.register(registrationStore.credentials).then((response) => {
        if (response.success) {
          router.push('healthcare').then(() => {
            createInProgress.value = false;
          });
        } else {
          throw response;
        }
      }).catch((e) => {
        const errorData = e?.response?.data || e;
        switch (errorData.errorKey) {
          case 'REGISTRATION_EMAIL_ALREADY_EXISTS':
            emailError.value = $localize('error.email.already.exists');
            break;
          case 'REGISTRATION_DATA_INVALID':
            emailError.value = $localize('error.email');
            isFormTouched.value = false;
            break;
          default:
            errorKey.value = $localize('error.account.incorrect');
            break;
        }
        createInProgress.value = false;
      });
    }

    onMounted(() => {
      MonitoringService.sendPageMountedSpeed();
      mainStore.resetAllStores();
      // if (!registrationStore.createUser?.npi && !registrationStore.createUser?.speciality) {
      //   router.push('step-4');
      // }
    });

    return {
      errorKey,
      mainStore,
      schema,
      emailValue,
      passwordValue,
      hippaValue,
      createInProgress,
      nextStep,
      inputEmail,
      emailError,
      passwordError,
      passwordRequirements,
      isFormTouched,
    };
  },
});
